import React, {createContext, useState, useRef, useMemo, useCallback, useEffect} from 'react';
import { useService } from 'hooks/useService';
import { PUT } from 'services/RequestMethods';
import { IMAGE_SUCCESS_STATUS_ID } from 'Enums/imageStatus';

export const CarouselContext = createContext();

const makeDefaultZoomObject = images => images?.reduce((acc, _, i) => ({ ...acc, [i]: { zoom: 1, x: 0, y: 0 }}), {});

export const CarouselProvider = ({
    children,
    images = [],
    enableRotate,
    handleReload = () => {},
    setLoading = () => {},
    setImage = () => {},
    userId,
    loading = false,
}) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imgTransform, setImgTransform] = useState(makeDefaultZoomObject(images));
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    const defaultCarouselRef = useRef(null);
    const fullscreenCarouselRef = useRef(null);
    const imageRefs = useRef({});
    const fsImageRefs = useRef({});

    const [{}, fireUpdateUserImage] = useService({
        method: PUT,
    });

    const setImg = (i) => {
        return {
            position: val => setImgTransform(prevImgTransform => ({
                ...prevImgTransform,
                [i]: { ...prevImgTransform[i], ...val,
                }})),
            zoom: val => setImgTransform(prevImgTransform => ({
                ...prevImgTransform,
                [i]: { ...prevImgTransform[i], zoom: val,
                }})),
        };
    };

    const galleryImages = useMemo(() => images?.map(img => ({
        original: img.image_path_giant,
        thumbnail: img.image_path_giant,
    })), [images]);

    const back = () => {
        if (currentIndex === 0) return;
        if (defaultCarouselRef.current) {
            defaultCarouselRef.current.slideLeft();
        }
        if (fullscreenCarouselRef.current) {
            fullscreenCarouselRef.current.slideLeft();
        }
    };

    const next = () => {
        if (currentIndex === images.length - 1) return;
        if (defaultCarouselRef.current) {
            defaultCarouselRef.current.slideRight();
        }
        if (fullscreenCarouselRef.current) {
            fullscreenCarouselRef.current.slideRight();
        }
    };
    const handleSlideChange = (index) => {
        setImgTransform(makeDefaultZoomObject(images));
        setCurrentIndex(index);
    };

    const startDrag = (e) => {
        const zoomFactor = imgTransform[currentIndex].zoom;
        
        if (zoomFactor === 1) return;

        // Adjust the starting drag position based on the current zoom level and image position
        setDragStart({
            x: e.clientX - (imgTransform[currentIndex].x * zoomFactor),
            y: e.clientY - (imgTransform[currentIndex].y * zoomFactor),
        });
        setIsDragging(true);
    };

    const onDrag = (e) => {
        if (isDragging) {
            // Calculate the new position of the image
            const { zoom } = imgTransform[currentIndex];

            const newX = (e.clientX - dragStart.x) / zoom;
            const newY = (e.clientY - dragStart.y) / zoom;

            // Update the position state
            setImg(currentIndex).position({ x: newX, y: newY });
        }
    };

    const endDrag = () => {
        setIsDragging(false);
    };


    const zoomIn = () => handleScroll({deltaY: -0.1});

    const zoomOut = () => handleScroll({deltaY: 0.1});

    const handleScroll = useCallback((event) => {
        event.preventDefault && event.preventDefault();
        event.stopPropagation && event.stopPropagation();

        const zoomDirection = event.deltaY < 0 ? 0.1 : -0.1;
        setImgTransform(prevImgTransform => {
            const { zoom: prevZoom, x: prevX, y: prevY } = prevImgTransform[currentIndex];
            const newZoom = Math.min(Math.max(prevZoom + zoomDirection, 1), 3);

            if (zoomDirection < 0 && newZoom > 1) {
                const zoomChangeRatio = newZoom / prevZoom;
                const newX = prevX * zoomChangeRatio;
                const newY = prevY * zoomChangeRatio;

                setImg(currentIndex).position({ x: newX, y: newY });
            } else if (newZoom === 1) {
                setImg(currentIndex).position({ x: 0, y: 0 });
            }

            return { ...prevImgTransform, [currentIndex]: {...prevImgTransform[currentIndex], zoom: newZoom } };
        });
    }, [currentIndex]);

    const handleRotate = async (dir) => {
        setLoading(true);

        await fireUpdateUserImage({
            url: `/users/check/identity/${userId}/images/${images[0].image_id}`,
            params: {
                action: 'rotate',
                direction: dir,
            },
        });

        const pollInterval = 1000;

        const poll = async () => {
            const { results } = await handleReload();
            const statusId = results[0].status_id;

            if (statusId !== IMAGE_SUCCESS_STATUS_ID) {
                setTimeout(poll, pollInterval);
            } else {
                setImage();
                setLoading(false);
            }
        };

        setTimeout(poll, pollInterval);
    };

    useEffect(() => {
        setImgTransform(makeDefaultZoomObject(images));

        if (isFullScreen) {
            document.body.classList.remove('modal-open');
        }
    }, [isFullScreen]);

    const contextValue = {
        currentIndex,
        setCurrentIndex,
        imgTransform,
        setImgTransform,
        isFullScreen,
        setIsFullScreen,
        defaultCarouselRef,
        fullscreenCarouselRef,
        galleryImages,
        setImg,
        back,
        next,
        zoomIn,
        zoomOut,
        handleSlideChange,
        startDrag,
        onDrag,
        endDrag,
        handleScroll,
        handleRotate,
        imageRefs,
        fsImageRefs,
        enableRotate,
        loading,
    };

    return (
        <CarouselContext.Provider value={contextValue}>
            {children}
        </CarouselContext.Provider>
    );
};